import PropTypes from 'prop-types';
import InteractionFormConsumer from './components/interaction-form-consumer';
import { InteractionFormProvider } from 'apps/interactions/form/context/interaction-form-context';

/**
 * The InteractionForm is used to create a new Interaction or edit an existing one.
 * See the propTypes object below for details about what props it accepts.
 *
 * Note: This component leverages Formik and DynamicForm, which are defined in the @evertrue/sdk package.
 *
 * @prop {editing} editing - A boolean indicating whether the form is in edit mode or not. Default is false.
 * @prop {interaction} interaction - The interaction object to be edited. If creating a new interaction, this will be an empty object. if editing is true, this prop is required.
 * @prop {contact} contact - The contact object associated with the interaction. If no contact is selected, this will be an empty object. NOTE: there will never be a "contact" prop and "contactOptions" prop at the same time.
 * @prop {contactOptions} contactOptions - An array of contact options to be used in the contact select field. NOTE: there will never be a "contact" prop and "contactOptions" prop at the same time.
 * @prop {referrer} referrer - The place where the user came from before opening the form. This is used for tracking purposes. Default is empty string.
 * @prop {requestHide} requestHide - A function to close the form. Required.
 * @prop {saveLabel} saveLabel - The label for the save button. Default is "Save".
 * @prop {cancelLabel} cancelLabel - The label for the cancel button. Default is "Cancel".
 * @prop {emailId} emailId - The ID of the email associated with the interaction. Optional.
 * @prop {emailCreatorId} emailCreatorId - The ID of the email creator associated with the interaction. Optional.
 * @prop {afterSubmit} afterSubmit - A function to be called after the form is submitted. Default is an empty function.
 */
const InteractionForm = ({
  editing = false,
  interaction = {},
  contact = {},
  contactOptions = [],
  referrer = '',
  requestHide = () => {},
  saveLabel = 'Save',
  cancelLabel = 'Cancel',
  afterSubmit = () => {},
}) => {
  // Wrap the form in the InteractionFormProvider to provide context to all fields
  // Most props don't actually require much drilling down, so we can just pass them directly to the child components.
  // If that changes for some reason, you can route them through the Provider and make them accessible anywhere.
  // (I did this with the "editing" prop as an example).
  return (
    <InteractionFormProvider editing={editing}>
      <InteractionFormConsumer
        editing={editing}
        existingInteraction={interaction}
        contact={contact}
        contactOptions={contactOptions}
        referrer={referrer}
        requestHide={requestHide}
        saveLabel={saveLabel}
        cancelLabel={cancelLabel}
        afterSubmit={afterSubmit}
      />
    </InteractionFormProvider>
  );
};

InteractionForm.propTypes = {
  editing: PropTypes.bool,
  interaction: PropTypes.object,
  contact: PropTypes.object,
  contactOptions: PropTypes.array,
  referrer: PropTypes.string,
  requestHide: PropTypes.func,
  saveLabel: PropTypes.any,
  cancelLabel: PropTypes.any,
  afterSubmit: PropTypes.func,
};

export default InteractionForm;
